import {
  Button,
  SearchField,
  ToggleButton,
  ToggleButtonGroup,
} from "@heroui/react";
import { useMemo, type ReactNode } from "react";

import { useTranslations } from "@/src/i18n/i18n";
import type { PostFilters } from "@/src/pages/Posts/hooks/usePostFilters";
import {
  DELETED_FILTER_KEY,
  DELETED_FILTERS,
  isDeletedFilter,
  isPostSort,
  isPostStatusFilter,
  isPostTypeFilter,
  SORT_KEY,
  SORTS,
  STATUS_FILTER_KEY,
  STATUS_FILTERS,
  TYPE_FILTER_KEY,
  TYPE_FILTERS,
} from "@/src/pages/Posts/posts.type";

type PostsFilterProps = {
  filters: PostFilters;
  term: string;
  onTermChange: (_term: string) => void;
  onChange: (_patch: Partial<PostFilters>) => void;
};

/**
 * La barre de filtres du fil : recherche, statut, nature, sort des publications
 * retirées, et tri.
 *
 * Des groupes de bascules et non des onglets, pour la même raison que
 * `ReportsFilter` : il n'y a **qu'un seul panneau**, que les filtres
 * re-remplissent. Des `role="tab"` promettraient des panneaux distincts à
 * parcourir au clavier, ce qui serait faux.
 *
 * ⚠️ **Les trois derniers groupes portent un libellé visible**, contrairement à
 * ceux de la liste des événements. Là-bas il n'y a que deux groupes, dont on
 * devine l'objet ; ici quatre bascules voisines dont trois se lisent « Tous »
 * seraient indiscernables les unes des autres.
 */
export function PostsFilter({
  filters,
  term,
  onTermChange,
  onChange,
}: PostsFilterProps) {
  const t = useTranslations("posts");
  const ta = useTranslations("a11y");

  // Mémoïsés : reconstruits à chaque rendu, les tableaux changent d'identité et
  // relancent la réconciliation des groupes pour rien.
  const statusKeys = useMemo(() => [filters.status], [filters.status]);
  const typeKeys = useMemo(() => [filters.type], [filters.type]);
  const deletedKeys = useMemo(() => [filters.deleted], [filters.deleted]);
  const sortKeys = useMemo(() => [filters.sort], [filters.sort]);

  return (
    <div className="flex flex-col gap-3">
      <SearchField
        aria-label={ta("search-posts")}
        fullWidth
        value={term}
        onChange={onTermChange}
      >
        <SearchField.Group>
          <SearchField.SearchIcon />
          <SearchField.Input placeholder={t("search-placeholder")} />
          {/* ⚠️ Le bouton d'effacement de HeroUI s'annonce « Clear » en anglais
              en dur — même piège que le `CloseButton`. */}
          <SearchField.ClearButton aria-label={ta("clear-search")} />
        </SearchField.Group>
      </SearchField>

      {/*
        ⚠️ **Le rail défile horizontalement, et ce n'est pas décoratif.** Cinq
        libellés anglais dépassent les 420 px d'un écran étroit : « Taken down »
        y était **coupé et inatteignable** — une file entière hors de portée. Le
        groupe ne se comprime pas (`w-max`), c'est le rail qui glisse. Les
        marges négatives laissent les pastilles atteindre le bord, et le
        rembourrage rend l'anneau de focus au clavier.
      */}
      <div className="-mx-1 overflow-x-auto px-1 py-1">
        <ToggleButtonGroup
          aria-label={ta("filter-posts")}
          className="w-max"
          disallowEmptySelection
          selectedKeys={statusKeys}
          selectionMode="single"
          size="sm"
          onSelectionChange={(keys) => {
            // `Key` vaut `string | number` : le groupe ne connaît pas notre
            // énumération, la garde la lui réapprend.
            for (const key of keys) {
              if (isPostStatusFilter(key)) onChange({ status: key });
            }
          }}
        >
          {STATUS_FILTERS.map((value) => (
            <ToggleButton id={value} key={value}>
              {t(STATUS_FILTER_KEY[value])}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </div>

      <div className="flex flex-wrap items-start gap-x-6 gap-y-3">
        <Group label={t("filter-type-label")}>
          <ToggleButtonGroup
            aria-label={ta("filter-post-types")}
            disallowEmptySelection
            selectedKeys={typeKeys}
            selectionMode="single"
            size="sm"
            onSelectionChange={(keys) => {
              for (const key of keys) {
                if (isPostTypeFilter(key)) onChange({ type: key });
              }
            }}
          >
            {TYPE_FILTERS.map((value) => (
              <ToggleButton id={value} key={value}>
                {t(TYPE_FILTER_KEY[value])}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Group>

        {/*
          ⚠️ **« Retirées par leur auteur » n'est pas un statut.** La suppression
          par un auteur est douce : la ligne survit avec son contenu, et c'est
          exprès — un compte qui publie et retire trois galeries douteuses en une
          semaine doit rester lisible ici. D'où un filtre à part, jamais une
          cinquième valeur de statut.
        */}
        <Group label={t("filter-deleted-label")}>
          <ToggleButtonGroup
            aria-label={ta("filter-deleted-posts")}
            disallowEmptySelection
            selectedKeys={deletedKeys}
            selectionMode="single"
            size="sm"
            onSelectionChange={(keys) => {
              for (const key of keys) {
                if (isDeletedFilter(key)) onChange({ deleted: key });
              }
            }}
          >
            {DELETED_FILTERS.map((value) => (
              <ToggleButton id={value} key={value}>
                {t(DELETED_FILTER_KEY[value])}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Group>

        <Group label={t("filter-sort-label")}>
          <div className="flex flex-wrap items-center gap-2">
            <ToggleButtonGroup
              aria-label={ta("sort-posts")}
              disallowEmptySelection
              selectedKeys={sortKeys}
              selectionMode="single"
              size="sm"
              onSelectionChange={(keys) => {
                for (const key of keys) {
                  if (isPostSort(key)) onChange({ sort: key });
                }
              }}
            >
              {SORTS.map((value) => (
                <ToggleButton id={value} key={value}>
                  {t(SORT_KEY[value])}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            {/* Le sens du tri est une bascule, pas un onglet de plus : il ne
                change pas *ce* qu'on trie, seulement par quel bout. */}
            <Button
              size="sm"
              variant="ghost"
              onPress={() =>
                onChange({ order: filters.order === "asc" ? "desc" : "asc" })
              }
            >
              {t(filters.order === "asc" ? "order-asc" : "order-desc")}
            </Button>
          </div>
        </Group>
      </div>
    </div>
  );
}

/** Un groupe de bascules, avec le mot qui dit ce qu'il filtre. */
function Group({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs text-muted">{label}</span>
      {children}
    </div>
  );
}
